/**
 * pulsebot/permissions.ts — the ONE place PulseBot's role gates live.
 *
 * Mirrors App.tsx's ProtectedRoute model exactly: flat per-capability role
 * allow-lists + a fail-closed membership check. The allow-lists are copied
 * verbatim from the matching routes so the bot grants exactly what the page does
 * — no more, no less.
 *
 * Used by:
 *   - create_invoice executor gate (canCreateInvoice)
 *   - the overdue-invoices / pending-procurement READ gates (handlers.ts)
 *   - the help registry role filter (capabilities shown only if usable)
 */

import type { UserRole } from '../../types';

/** Roles that may view/use Finance — copied from the /finance/* ProtectedRoute. */
export const FINANCE_ROLES: readonly UserRole[] = [
  'super_admin', 'hr_admin', 'dept_admin', 'finance_admin',
];

/** Roles that may view/use Procurement — copied from /admin/procurement. */
export const PROCUREMENT_ROLES: readonly UserRole[] = [
  'super_admin', 'hr_admin', 'dept_admin',
];

/**
 * Roles that may request a user invite — copied from /admin/users (super_admin
 * only). The strictest gate in the bot: user management is super_admin territory.
 */
export const USER_INVITE_ROLES: readonly UserRole[] = ['super_admin'];

/**
 * Fail-closed role-membership check: false when role is null/undefined,
 * otherwise whether it's in the allow-list. The single primitive every gate
 * (read or write) is built on.
 */
export function hasAnyRole(role: UserRole | null | undefined, allowed: readonly UserRole[]): boolean {
  return role != null && allowed.includes(role);
}

/**
 * True if the role may create invoices (finance-only; null → false). Mirrors the
 * /finance/invoices ProtectedRoute. Now a thin alias over hasAnyRole + the shared
 * FINANCE_ROLES list (behavior identical to the prior dialog.ts implementation).
 */
export function canCreateInvoice(role: UserRole | null | undefined): boolean {
  return hasAnyRole(role, FINANCE_ROLES);
}

/**
 * True if the role may request a user invite (super_admin only; null → false).
 * Mirrors the /admin/users ProtectedRoute — the strictest bot gate.
 */
export function canRequestInvite(role: UserRole | null | undefined): boolean {
  return hasAnyRole(role, USER_INVITE_ROLES);
}
